import { useTranslation } from "react-i18next";
import { Chip, StatusGlyph } from "@outpave/ds";
import type { ChipTone } from "@outpave/ds";
import { SubscriptionStatus } from "enums/Subscription";

type StatusKey = SubscriptionStatus | "AWAITING_PAYMENT_METHOD" | "default";

const statusMapping: Record<
  StatusKey,
  { glyph: string; name: string; tone: ChipTone }
> = {
  [SubscriptionStatus.ACTIVE]: {
    glyph: "active",
    name: "Active",
    tone: "positive",
  },
  [SubscriptionStatus.INCOMPLETE]: {
    glyph: "pending",
    name: "Setting Up",
    tone: "info",
  },
  [SubscriptionStatus.PAST_DUE]: {
    glyph: "overdue",
    name: "Past Due",
    tone: "warning",
  },
  [SubscriptionStatus.CANCELED]: {
    glyph: "canceled",
    name: "Canceled",
    tone: "neutral",
  },
  [SubscriptionStatus.COMPLETED]: {
    glyph: "completed",
    name: "Completed",
    tone: "positive",
  },
  // Internal statuses remain here only to keep the map exhaustive.
  [SubscriptionStatus.SUPERSEDED]: {
    glyph: "pending",
    name: "Setting Up",
    tone: "info",
  },
  AWAITING_PAYMENT_METHOD: {
    glyph: "failed",
    name: "Needs Payment Method",
    tone: "warning",
  },
  default: { glyph: "pending", name: "Unknown Status", tone: "neutral" },
};

interface RenderSubscriptionStatusProps {
  status: SubscriptionStatus;
  awaitingPaymentMethod?: boolean | null;
  textOnly?: boolean;
}

const RenderSubscriptionStatus: React.FC<RenderSubscriptionStatusProps> = ({
  status,
  awaitingPaymentMethod,
  textOnly = false,
}) => {
  const { t } = useTranslation();

  const key: StatusKey =
    awaitingPaymentMethod && status === SubscriptionStatus.PAST_DUE
      ? "AWAITING_PAYMENT_METHOD"
      : status;

  const { glyph, name, tone } = statusMapping[key] || statusMapping.default;

  if (textOnly) {
    return <>{t(name)}</>;
  }

  return (
    <Chip
      tone={tone}
      size="sm"
      label={t(name)}
      leading={<StatusGlyph status={glyph} tone={tone} />}
    />
  );
};

export default RenderSubscriptionStatus;
